
import { formatFullName } from '@/lib/format';
import { getPrayerCategories } from '@/lib/categories';
import { Badge, Button, Card, CardFooter, getCategoryBadgeVariant } from '@/components/ui';
import type { QueueItem } from '@/hooks/useProducerQueue';
import type { PrayerRequest } from '@/types';

export interface ShuffleHeroCardProps {
  item: QueueItem | null;
  queueType: 'prayer' | 'praise';
  /** Optional label for combined mix (e.g. Prayer Request / Answered Prayer) */
  typeLabel?: string;
  /** Optional: show Display Again button when already displayed */
  onDisplayAgain?: (prayerRequestId: string) => void;
  onEdit?: (prayer: PrayerRequest) => void;
  onRemove?: (prayerId: string) => void;
}

export function ShuffleHeroCard({ item, typeLabel, onDisplayAgain, onEdit, onRemove }: ShuffleHeroCardProps) {
  if (!item) {
    return (
      <Card
        padding="lg"
        className="border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/30 text-center py-12"
      >
        <p className="text-slate-500 dark:text-slate-400 font-medium">Nothing live</p>
        <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
          Select a card below or start shuffle to display
        </p>
      </Card>
    );
  }

  const { prayerRequest, prayerRequestId, displayCount = 0 } = item;
  if (!prayerRequest) {
    return (
      <Card padding="lg" className="border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-900/10 text-center py-12">
        <p className="text-slate-600 dark:text-slate-400 font-medium">Incomplete queue row</p>
      </Card>
    );
  }
  const prayerCategories = getPrayerCategories(prayerRequest ?? {});
  const categoryLabels = prayerCategories.length > 0 ? prayerCategories : ['General'];
  const displayName = prayerRequest?.isAnonymous
    ? 'Anonymous'
    : (formatFullName(prayerRequest?.firstName ?? null, prayerRequest?.lastName ?? null) || 'Anonymous');
  return (
    <Card
      padding="md"
      variant="elevated"
      className="relative border-l-4 border-l-primary-500"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {typeLabel ? (
            <Badge variant="info" className="capitalize">
              {typeLabel}
            </Badge>
          ) : null}
          {categoryLabels.map((cat) => (
            <Badge key={cat} variant={getCategoryBadgeVariant(cat)} className="capitalize">
              {cat}
            </Badge>
          ))}
          <span className="text-slate-600 dark:text-slate-400">{displayName}</span>
        </div>
      </div>

      <p className="mb-4 whitespace-pre-wrap text-base text-slate-800 dark:text-slate-200 line-clamp-4">
        {prayerRequest?.body ?? ''}
      </p>

      <p className="text-sm text-slate-500 dark:text-slate-400">
        Displayed {displayCount} time{displayCount === 1 ? '' : 's'}
      </p>

      <CardFooter className="mt-4 justify-between">
        <div className="flex items-center gap-2">
          {onDisplayAgain && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDisplayAgain(prayerRequestId)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
            >
              Display again
            </Button>
          )}
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(prayerRequest)}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              }
            >
              Edit
            </Button>
          )}
        </div>
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(prayerRequestId)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            leftIcon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            }
          >
            Remove
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
